import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  ScoreBar,
  Spinner,
  type Editor,
} from '@aigovops/design-system';
import { useRequiredProfile } from '../lib/auth';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { DeltaChip } from '../components/DeltaChip';
import {
  getAssessment,
  getControlCoverage,
  getOutcomeScores,
  getProfile,
  getReadinessHistory,
  getRoadmap,
  listComplianceTargets,
  listRegisterAssignees,
  req,
} from '../lib/api';
import { OutcomeCard } from '../components/dashboard/OutcomeCard';
import { CountdownBadge } from '../components/dashboard/CountdownBadge';
import { NextDeadlineStrip } from '../components/dashboard/NextDeadlineStrip';
import { Soc2Tile, useSoc2Tile } from '../components/dashboard/Soc2Tile';
import { soc2Verdict, windowStatus } from '../lib/soc2';
import { VerdictPill } from './Soc2Readiness';
import { PendingPoliciesRow } from '../components/PendingPolicies';
import type { Verdict } from '../lib/soc2';

/** The workspace-level SOC 2 framework row (not SOC 2 + AI). */
const isSoc2Framework = (fs: { framework_id: string; framework_name: string }) => /^(fw-)?soc2$/i.test(fs.framework_id) || fs.framework_name === 'SOC 2';
import { FirstRunQuestionnaireBanner } from '../components/dashboard/FirstRunQuestionnaireBanner';
import { classificationLabel, daysUntil } from '../lib/format';
import { useOptionalWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import { ProfileHealthCard, computeProfileGaps } from '../components/ProfileHealthCard';
import { OnboardingChecklist } from '../components/OnboardingChecklist';

const DASHBOARD_REVIEWERS: Editor[] = [
  {
    id: 's-nielsen',
    displayName: 'Steve Nielsen',
    profileUrl: '/editors',
    credentials: ['GRC lead — see /editors for full masthead'],
    domains: ['aggregate'],
    verified: true,
  },
];

export function Dashboard() {
  const profileId = useRequiredProfile();
  const { user } = useAuth();
  const assess = useWorkspaceQuery(
    (s) => queryKeys.dashboard(s),
    () => getAssessment(profileId),
  );
  const outcomes = useWorkspaceQuery(
    (s) => queryKeys.outcomes(s),
    () => getOutcomeScores(profileId),
  );
  const profile = useWorkspaceQuery(
    (s) => queryKeys.profile(s),
    () => getProfile(profileId),
  );
  const workspaceCtx = useOptionalWorkspace();
  const workspaceSlug = workspaceCtx?.workspace.slug ?? null;
  const prefix = workspaceCtx ? `/w/${workspaceCtx.workspace.slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  const targetsAsync = useWorkspaceQuery(
    (s) => queryKeys.complianceTargets(s),
    (s) => listComplianceTargets(s),
  );
  const historyAsync = useWorkspaceQuery(
    (s) => queryKeys.readinessHistory(s, profileId),
    (s) => getReadinessHistory(s, profileId, '8w'),
  );
  const assigneesAsync = useWorkspaceQuery(
    (s) => queryKeys.registerAssignees(s),
    (s) => listRegisterAssignees(s),
  );
  const roadmapAsync = useWorkspaceQuery(
    (s) => queryKeys.roadmap(s),
    () => getRoadmap(profileId, 5),
  );
  const controlCoverageAsync = useWorkspaceQuery(
    (s) => queryKeys.controlCoverage(s),
    (s) => getControlCoverage(s),
  );
  const isAssessor = workspaceCtx?.role === 'assessor';
  // V96-4-9: when the SOC 2 tile renders it is the one SOC 2 number on the page.
  const soc2Tile = useSoc2Tile(workspaceSlug ?? '');
  const editProfileHref = workspaceCtx
    ? `/w/${workspaceCtx.workspace.slug}/onboarding?mode=edit`
    : '/onboarding?mode=edit';

  const myOpenCount = useMemo(() => {
    if (!assess.data || !assigneesAsync.data) return 0;
    const assigneeByItem = new Map(
      assigneesAsync.data.assignees.map((r) => [r.register_item_id, r.assigned_to])
    );
    return assess.data.gap_rows.filter(
      (r) =>
        assigneeByItem.get(r.item_id)?.user_id === user?.id &&
        (r.status === 'todo' || r.status === 'in_progress')
    ).length;
  }, [assess.data, assigneesAsync.data, user?.id]);

  const sparklineData = useMemo(() => {
    const points = historyAsync.data?.points ?? [];
    return points.map((p) => p.as_shipped.score);
  }, [historyAsync.data]);

  /** V3-8-4: week-over-week readiness score delta from history points. */
  const readinessScoreDelta = useMemo((): number | null => {
    const points = historyAsync.data?.points ?? [];
    if (points.length < 2) return null;
    const latest = points[points.length - 1].as_shipped.score;
    // Find the point closest to 7 days ago
    const latestDate = new Date(points[points.length - 1].taken_at).getTime();
    const targetDate = latestDate - 7 * 24 * 60 * 60 * 1000;
    let closest = points[0];
    let closestDist = Math.abs(new Date(closest.taken_at).getTime() - targetDate);
    for (let i = 1; i < points.length - 1; i++) {
      const dist = Math.abs(new Date(points[i].taken_at).getTime() - targetDate);
      if (dist < closestDist) {
        closest = points[i];
        closestDist = dist;
      }
    }
    return latest - closest.as_shipped.score;
  }, [historyAsync.data]);

  if (assess.loading || outcomes.loading || profile.loading) return <Spinner label="Loading readiness" />;
  if (assess.error) return <div style={{ display: 'grid', gap: 'var(--space-3)' }}><Alert variant="error">{assess.error}</Alert><Button onClick={() => assess.refetch()}>Retry</Button></div>;
  if (outcomes.error) return <div style={{ display: 'grid', gap: 'var(--space-3)' }}><Alert variant="error">{outcomes.error}</Alert><Button onClick={() => outcomes.refetch()}>Retry</Button></div>;
  if (profile.error) return <div style={{ display: 'grid', gap: 'var(--space-3)' }}><Alert variant="error">{profile.error}</Alert><Button onClick={() => profile.refetch()}>Retry</Button></div>;
  if (!assess.data || !outcomes.data || !profile.data) return null;

  const a = assess.data;
  const { applicable_frameworks } = profile.data;
  const targetsByFramework = new Map(
    (targetsAsync.data?.targets ?? []).map((t) => [t.framework_id, t])
  );
  const hasSystems = a.classification_maturity.length > 0;
  // V96-4-9 round 3: the rows under the hero figure, SOC 2 at the tile's criteria readiness,
  // each with the same verdict tone the frameworks table uses. The hero figure is their mean.
  const tileVerdict = soc2Tile.state === 'ready' && soc2Tile.data ? soc2Tile.verdict : null;
  const heroRows = a.framework_scores.map((fs) => {
    if (tileVerdict && soc2Tile.data && isSoc2Framework(fs)) return { ...fs, score: Math.round(soc2Tile.data.readiness_pct), tone: tileVerdict.tone };
    return { ...fs, tone: bandVerdict(bandFor(fs.score)).tone };
  });
  const openCount = a.totals.applicable - a.totals.done - a.totals.not_applicable;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <PageHeader
        title="Readiness"
        subtitle={
          <>
            {a.framework_scores.length || applicable_frameworks.length} frameworks in scope · {a.totals.applicable} applicable items
          </>
        }
        freshness={{
          updatedAt: a.generated_at,
          source: `${a.framework_scores.length} frameworks`,
          reviewer: DASHBOARD_REVIEWERS,
        }}
        right={
          isAssessor ? undefined : (
            <Link to={editProfileHref}>
              <Button variant="secondary" compact>
                Edit scope
              </Button>
            </Link>
          )
        }
      />

      {workspaceSlug && !isAssessor ? <FirstRunQuestionnaireBanner slug={workspaceSlug} /> : null}
      {workspaceSlug && !isAssessor ? <PendingPoliciesRow slug={workspaceSlug} /> : null}

      {workspaceSlug ? <LaneKpiStrip slug={workspaceSlug} prefix={prefix} /> : null}
      {workspaceSlug && !isAssessor ? <OnboardingChecklist slug={workspaceSlug} /> : null}

      {workspaceSlug && !isAssessor ? (
        <ProfileHealthCard
          gaps={computeProfileGaps(profile.data.profile, hasSystems ? 1 : 0)}
          workspaceSlug={workspaceSlug}
        />
      ) : null}

      {/* Assessor engagement summary — PR-P6-2 */}
      {isAssessor && workspaceCtx?.scopeBar ? (
        <section
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-4)',
            fontFamily: 'var(--font-ui)',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>
            Engagement overview
          </div>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', marginTop: 'var(--space-1)' }}>
            Read-only assessment view. Use Q&amp;A threads and evidence requests to interact with the workspace team.
          </div>
        </section>
      ) : null}

      {/* Your queue strip — UC-2 R2 */}
      {myOpenCount > 0 ? (
        <section
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>
            {myOpenCount} item{myOpenCount !== 1 ? 's' : ''} assigned to you
          </span>
          <Link
            to={`${prefix}/mine`}
            style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 500, fontSize: 'var(--font-size-3xs)' }}
          >
            View your queue →
          </Link>
        </section>
      ) : null}

      {/* Hero KPI row — UC-2 R3, NAV-6-1 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: 'var(--space-4)',
        }}
      >
        <HeroKpi
          score={heroRows.length ? Math.round(heroRows.reduce((sum, fs) => sum + fs.score, 0) / heroRows.length) : a.aggregate_score}
          openCount={openCount}
          doneCount={a.totals.done}
          applicableCount={a.totals.applicable}
          // History tracks the assessment's aggregate, not this average: no trend beside it (backlog: readiness history).
          sparklineData={heroRows.length ? [] : sparklineData}
          scoreDelta={heroRows.length ? null : readinessScoreDelta}
          frameworkScores={heroRows}
          registerHref={`${prefix}/register`}
        />
        {workspaceSlug ? <Soc2Tile slug={workspaceSlug} /> : null}
        <NextDeadlineStrip
          targets={targetsAsync.data?.targets ?? []}
          frameworkNames={new Map(a.framework_scores.map((fs) => [fs.framework_id, fs.framework_name]))}
        />
      </div>

      {/* Delta strip — UC-2 R3 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        <DeltaCell label="Done" value={a.totals.done} sentiment="positive" />
        <DeltaCell label="In progress" value={a.totals.in_progress} sentiment="neutral" />
        <DeltaCell label="Blocked" value={a.totals.blocked} color="var(--critical)" sentiment="negative" />
        <DeltaCell label="Open" value={openCount} sentiment="negative" />
      </div>

      {/* V91-4-4: Controls addressed metric card */}
      {workspaceSlug && !controlCoverageAsync.loading && controlCoverageAsync.data ? (
        <ControlCoverageCard
          addressed={controlCoverageAsync.data.addressed}
          total={controlCoverageAsync.data.total}
          systemsExist={hasSystems}
          prefix={prefix}
        />
      ) : null}

      {/* Top priority gaps — UC-2 R2/R3, PR-P5-3 */}
      {roadmapAsync.data && roadmapAsync.data.items.length > 0 ? (
        <section aria-label="Top priority gaps">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
            <h2 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)' }}>
              Top priority gaps
            </h2>
            <Link
              to={`${prefix}/roadmap`}
              style={{ color: 'var(--accent)', fontSize: 'var(--font-size-3xs)', textDecoration: 'none' }}
            >
              Full roadmap →
            </Link>
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {roadmapAsync.data.items.slice(0, 5).map((item, idx) => {
              const days = daysUntil(item.deadline, new Date());
              const urgentColor = days !== null && days < 14 ? 'var(--critical)' : days !== null && days < 30 ? 'var(--warning-fg)' : 'var(--text-dim)';
              return (
                <Link
                  key={item.register_item_ref}
                  to={`${prefix}/register/${item.register_item_ref}`}
                  style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    alignItems: 'center',
                    padding: 'var(--space-3) var(--space-4)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-card)',
                    background: 'var(--surface)',
                    textDecoration: 'none',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--surface-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-3xs)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-dim)', flexShrink: 0 }}>
                    {idx + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: 'var(--font-size-2xs)', fontWeight: 500 }}>
                    {item.title}
                  </span>
                  {days !== null ? (
                    <span style={{ fontSize: 'var(--font-size-3xs)', fontFamily: 'var(--font-mono)', color: urgentColor, fontWeight: 600, flexShrink: 0 }}>
                      {days < 0 ? `${-days}d overdue` : `${days}d`}
                    </span>
                  ) : null}
                  <span style={{ color: 'var(--accent)', fontSize: 'var(--font-size-3xs)', flexShrink: 0 }}>→</span>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Framework scores — enhanced with trajectory */}
      <section aria-label="Frameworks in scope">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h2 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)' }}>
            Frameworks in scope
          </h2>
          <Link
            to={`${prefix}/register`}
            style={{ color: 'var(--accent)', fontSize: 'var(--font-size-3xs)', textDecoration: 'none' }}
          >
            Open register →
          </Link>
          <Link
            to={`${prefix}/attested-capabilities`}
            style={{ color: 'var(--accent)', fontSize: 'var(--font-size-3xs)', textDecoration: 'none' }}
          >
            Attested capabilities →
          </Link>
        </div>
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
                color: 'var(--text)',
                borderCollapse: 'collapse',
              }}
            >
              <thead style={{ background: 'var(--surface-elevated)', color: 'var(--text-dim)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Framework</th>
                  <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Progress</th>
                  <th style={{ textAlign: 'right', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Score</th>
                  <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {a.framework_scores.map((fs) => {
                  const band = bandFor(fs.score);
                  const target = targetsByFramework.get(fs.framework_id);
                  // V96-4-9: one SOC 2 number on the dashboard — when the SOC 2
                  // tile renders, this row shows the same criteria-based readiness.
                  const soc2 = soc2Tile.state === 'ready' && soc2Tile.data && isSoc2Framework(fs) ? soc2Tile.data : null;
                  if (soc2) {
                    const pct = Math.round(soc2.readiness_pct);
                    const verdict = soc2Tile.verdict ?? soc2Verdict(soc2.counts, soc2Tile.ctx.period ? windowStatus(soc2Tile.ctx.period) : null);
                    return (
                      <tr key={fs.framework_id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                          <Link to={`${prefix}/soc2`} style={{ fontWeight: 500, color: 'var(--text)' }}>{fs.framework_name}</Link>
                          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                            {soc2.counts.met} of {soc2.counts.total} criteria met
                          </div>
                        </td>
                        <td style={{ padding: 'var(--space-2) var(--space-3)', minWidth: '120px' }}>
                          <ScoreBar score={pct} tone={verdict.tone} hideLabel />
                        </td>
                        <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                          {pct}
                        </td>
                        <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                          <VerdictPill verdict={verdict} />
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={fs.framework_id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                        <div style={{ fontWeight: 500 }}>{fs.framework_name}</div>
                        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                          {fs.complete_count} of {fs.applicable_items} complete
                          {target ? <> · <CountdownBadge target={target} /></> : null}
                        </div>
                      </td>
                      <td style={{ padding: 'var(--space-2) var(--space-3)', minWidth: '120px' }}>
                        <ScoreBar score={fs.score} tone={band === 'green' ? 'success' : band === 'amber' ? 'warning' : 'danger'} hideLabel />
                      </td>
                      <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                        {fs.score}
                      </td>
                      <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                        <VerdictPill verdict={bandVerdict(band)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'right' }}>
          <a
            href={a.methodology.formula_url}
            title={`Effective ${new Date(a.methodology.effective_at).toLocaleDateString()}`}
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-dim)',
              textDecoration: 'none',
              fontFamily: 'var(--font-mono)',
            }}
          >
            How is this scored? methodology v{a.methodology.version}
          </a>
        </div>
      </section>

      {/* Supplier rollup panel — PR-P5-4 (owner/admin only) */}
      {isAssessor ? null : <section
        aria-label="Suppliers in scope"
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>Suppliers in scope</h2>
          <Link
            to={`${prefix}/suppliers`}
            style={{ color: 'var(--accent)', fontSize: 'var(--font-size-3xs)', textDecoration: 'none' }}
          >
            Open supplier portfolio →
          </Link>
        </div>
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-dim)' }}>
          Supplier risk tracking populates when vendor assessments are configured.
        </div>
      </section>}

      {/* Readiness outcomes */}
      <section aria-label="Readiness outcomes">
        <h2 style={{ margin: '0 0 var(--space-3)', fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)' }}>
          Readiness outcomes
        </h2>
        {outcomes.data.outcomes.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gap: 'var(--space-4)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            }}
          >
            {outcomes.data.outcomes.map((o) => (
              <OutcomeCard key={o.outcome_id} outcome={o} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No outcomes scored yet"
            description="Outcomes score against your AI systems' maturity levels. Add your first AI system to start populating this view."
            action={
              <Link to={`${prefix}/systems`}>
                <Button variant="primary" compact>
                  Add AI system
                </Button>
              </Link>
            }
          />
        )}
      </section>

      {/* Classification maturity */}
      <section aria-label="Classification maturity">
        <h2 style={{ margin: '0 0 var(--space-3)', fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)' }}>
          Classification maturity
        </h2>
        {hasSystems ? (
          <div
            style={{
              display: 'grid',
              gap: 'var(--space-3)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            }}
          >
            {a.classification_maturity.map((cm) => (
              <div
                key={cm.classification}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)',
                  padding: 'var(--space-4)',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)' }}>
                    {classificationLabel[cm.classification]}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                    {cm.implemented}/{cm.applicable_items} items
                  </div>
                </div>
                <div style={{ marginTop: 'var(--space-2)', display: 'flex', gap: 'var(--space-1)' }}>
                  {[1, 2, 3, 4, 5].map((lvl) => (
                    <div
                      key={lvl}
                      title={`Level ${lvl}`}
                      style={{
                        flex: 1,
                        height: '10px',
                        borderRadius: 'var(--radius-input)',
                        background: lvl <= cm.maturity_level ? 'var(--accent)' : 'var(--border)',
                      }}
                    />
                  ))}
                </div>
                <div style={{ marginTop: '4px', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                  Maturity level {cm.maturity_level} of 5
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No AI systems yet"
            description="Add your first AI system to see maturity by classification."
            action={
              <Link to={`${prefix}/systems`}>
                <Button variant="primary" compact>
                  Add system
                </Button>
              </Link>
            }
          />
        )}
      </section>
    </div>
  );
}

// ---- Hero KPI -----------------------------------------------------------

type HeroBand = 'green' | 'amber' | 'red';

function bandFor(score: number): HeroBand {
  if (score >= 70) return 'green';
  if (score >= 40) return 'amber';
  return 'red';
}

const HERO_BAND_STYLE: Record<HeroBand, { bg: string; border: string; label: string; labelText: string }> = {
  green: { bg: 'var(--success-bg)', border: 'var(--band-green)', label: 'On track', labelText: 'var(--success-fg)' },
  amber: { bg: 'var(--warning-bg)', border: 'var(--band-amber)', label: 'At risk', labelText: 'var(--warning-fg)' },
  red: { bg: 'var(--danger-bg)', border: 'var(--band-red)', label: 'Blocker', labelText: 'var(--danger-fg)' },
};

/** One pill vocabulary for every framework row: band → verdict (same component as SOC 2). */
function bandVerdict(band: HeroBand): Verdict {
  if (band === 'green') return { key: 'on_track', label: 'On track', glyph: 'check', tone: 'success' };
  if (band === 'amber') return { key: 'at_risk', label: 'At risk', glyph: 'warning', tone: 'warning' };
  return { key: 'off_track', label: HERO_BAND_STYLE.red.label, glyph: 'x', tone: 'danger' };
}

const TONE_FILL: Record<Verdict['tone'], string> = { success: 'var(--success)', warning: 'var(--warning)', danger: 'var(--critical)', neutral: 'var(--text-dim)' };

interface FrameworkScoreRow {
  framework_id: string;
  framework_name: string;
  score: number;
  applicable_items: number;
  done: number;
  /** Verdict tone for the bar — the same tone the frameworks table shows. */
  tone?: Verdict['tone'];
}

function HeroKpi(props: {
  score: number;
  openCount: number;
  doneCount: number;
  applicableCount: number;
  sparklineData: number[];
  scoreDelta: number | null;
  frameworkScores: FrameworkScoreRow[];
  registerHref: string;
}) {
  const band = bandFor(props.score);
  const style = HERO_BAND_STYLE[band];

  return (
    <Link
      to={props.registerHref}
      aria-label="Aggregate readiness — open register"
      style={{
        /* NAV-6-1: span full width of the grid */
        gridColumn: '1 / -1',
        border: `1px solid ${style.border}`,
        borderLeftWidth: '4px',
        borderRadius: 'var(--radius-card)',
        background: style.bg,
        padding: 'var(--space-5) var(--space-6)',
        display: 'grid',
        gap: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <div>
          <span
            style={{
              fontSize: 'var(--font-size-3xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: style.labelText,
              fontWeight: 700,
            }}
          >
            {style.label}
          </span>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', marginLeft: 'var(--space-2)' }}>
            Overall · average of {props.frameworkScores.length} framework{props.frameworkScores.length === 1 ? '' : 's'}
          </span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: 'var(--font-size-3xl, 3.5rem)', lineHeight: 1, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {props.score}
            </span>
            <span style={{ fontSize: 'var(--font-size-md)', color: 'var(--text-muted)', fontWeight: 500 }}>
              / 100
            </span>
            <DeltaChip value={props.scoreDelta} sentiment="positive" label="vs last week" />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-2)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            <span>{props.doneCount} of {props.applicableCount} complete</span>
            <span>{props.openCount} open</span>
          </div>
        </div>
        {props.sparklineData.length >= 2 ? (
          <MiniSparkline data={props.sparklineData} width={120} height={40} />
        ) : null}
      </div>

      {/* NAV-6-1: Per-framework readiness breakdown */}
      {props.frameworkScores.length > 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {props.frameworkScores.map((fs) => (
            <div
              key={fs.framework_id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px auto',
                alignItems: 'center',
                gap: 'var(--space-3)',
                fontSize: 'var(--font-size-3xs)',
              }}
            >
              <span style={{ color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fs.framework_name}
              </span>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: 'var(--border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(100, Math.max(0, fs.score))}%`,
                    borderRadius: 3,
                    background: fs.tone ? TONE_FILL[fs.tone] : 'var(--accent)',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)', minWidth: '3ch', textAlign: 'right' }}>
                {fs.score}%
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Link>
  );
}

// ---- Mini sparkline (inline SVG) ----------------------------------------

function MiniSparkline({ data, width, height }: { data: number[]; width: number; height: number }) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 100);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pad = 2;
  const points = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (width - 2 * pad);
    const y = pad + (1 - (v - min) / range) * (height - 2 * pad);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Readiness trend: ${data.join(', ')}`}
      style={{ flexShrink: 0 }}
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---- Delta cell ---------------------------------------------------------

function DeltaCell({ label, value, color, delta, sentiment }: {
  label: string;
  value: number;
  color?: string;
  delta?: number | null;
  sentiment?: 'positive' | 'negative' | 'neutral';
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-3)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: color ?? 'var(--text)', fontFamily: 'var(--font-mono)' }}>
          {value}
        </span>
        <DeltaChip value={delta ?? null} sentiment={sentiment ?? 'neutral'} />
      </div>
    </div>
  );
}

// ---- DeltaChip — extracted to ../components/DeltaChip.tsx (V3-8-7) ------

// ---- Verdict chip -------------------------------------------------------


// ---- V4-4-5: Lane-split KPI strip ----------------------------------------

function LaneKpiStrip({ slug: _slug, prefix }: { slug: string; prefix: string }) {
  const q = useWorkspaceQuery(
    (s) => queryKeys.systems(s),
    async (s) => {
      let body: Record<string, unknown>;
      try {
        body = await req<Record<string, unknown>>(`/api/w/${encodeURIComponent(s)}/systems`);
      } catch {
        return { buying: 0, building: 0 };
      }
      const systems = Array.isArray(body?.data) ? body.data : (body?.systems as Array<Record<string, unknown>>) ?? [];
      let buying = 0;
      let building = 0;
      for (const sys of systems) {
        if (sys.lane === 'buying') buying++;
        else building++;
      }
      return { buying, building };
    },
  );

  if (q.loading || q.error || !q.data) return null;
  const { buying, building } = q.data;
  if (buying === 0 && building === 0) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-3)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <Link
        to={`${prefix}/systems`}
        style={{
          border: '1px solid var(--border)',
          borderLeft: '4px solid var(--buying)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-3) var(--space-4)',
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Buying
        </div>
        <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
          {buying}
        </div>
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          {buying === 1 ? 'system' : 'systems'} tracked
        </div>
      </Link>
      <Link
        to={`${prefix}/systems`}
        style={{
          border: '1px solid var(--border)',
          borderLeft: '4px solid var(--building)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-3) var(--space-4)',
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Building
        </div>
        <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
          {building}
        </div>
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          {building === 1 ? 'system' : 'systems'} tracked
        </div>
      </Link>
    </div>
  );
}

// ---- V91-4-4: Control coverage card ------------------------------------

function ControlCoverageCard({
  addressed,
  total,
  systemsExist,
  prefix,
}: {
  addressed: number;
  total: number;
  systemsExist: boolean;
  prefix: string;
}) {
  if (!systemsExist) return null;

  const pct = total > 0 ? Math.round((addressed / total) * 100) : 0;

  return (
    <Link
      to={`${prefix}/systems`}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Controls addressed
      </div>
      {total === 0 ? (
        <div
          style={{
            marginTop: 'var(--space-2)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          No controls populated yet
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-1)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 700,
                color: 'var(--text)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {addressed}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-2xs)',
                color: 'var(--text-muted)',
                fontWeight: 500,
              }}
            >
              / {total}
            </span>
          </div>
          {/* Progress bar */}
          <div
            style={{
              marginTop: 'var(--space-2)',
              height: 6,
              borderRadius: 3,
              background: 'var(--border)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${pct}%`,
                borderRadius: 3,
                background: 'var(--accent)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <div
            style={{
              marginTop: '4px',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-dim)',
            }}
          >
            {pct}% coverage
          </div>
        </>
      )}
    </Link>
  );
}
